import { isWeb } from '@gluestack-ui/nativewind-utils/IsWeb';
import { Image } from 'expo-image';
import { X } from 'lucide-react-native';
import type React from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Dimensions, Platform, Pressable } from 'react-native';
import { twJoin } from 'tailwind-merge';

import { Tutorial_1, Tutorial_2, Tutorial_3, Tutorial_4 } from '@/assets/images';
import {
  Actionsheet,
  ActionsheetBackdrop,
  ActionsheetContent,
  ActionsheetDragIndicator,
  ActionsheetDragIndicatorWrapper,
  ActionsheetScrollView,
} from '@/components/ui/actionsheet';
import { Button, ButtonText, ButtonSpinner } from '@/components/ui/button';
import { Heading } from '@/components/ui/heading';
import { HStack } from '@/components/ui/hstack';
import { Icon } from '@/components/ui/icon';
import {
  Modal,
  ModalBackdrop,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from '@/components/ui/modal';
import { Text } from '@/components/ui/text';
import { VStack } from '@/components/ui/vstack';
import { ANALYTICS_EVENTS } from '@/constants/analytics';
import { segmentClient } from '@/util/analytics';

type TutorialStep = {
  title: string;
  body: string | React.ReactNode;
  heading?: string;
};

export type OnboardingTutorialModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onComplete?: () => void;
  steps?: TutorialStep[];
  loadingPrimaryAction?: boolean;
  markShown?: () => Promise<void>;
  markShowLater?: () => Promise<void>;
  /**
   * Stamped onto every tutorial event so completion can be read per user type.
   * The steps are written for submitters, and whether experts finish them is
   * the question a rewrite starts from — an answer that only exists if the
   * property is being collected before anyone asks, since analytics cannot be
   * backfilled.
   */
  userType?: string;
};

const welcomeStep: TutorialStep = {
  title: 'Welcome to Primum',
  heading: 'A safe, secure community for you and your peers.',
  body: "Primum connects you with oncology subspecialists across every major cancer type. Ask the questions you'd ask a trusted colleague.",
};

const remainingSteps: TutorialStep[] = [
  {
    title: 'Find the right expert instantly',
    heading: '100+ oncology subspecialists, ready to collaborate.',
    body: 'Browse the directory or let Primum match you automatically. Every expert is a vetted academic clinician, available to share insights in real time.',
  },
  {
    title: 'Learn from real-world cases',
    heading: 'Insights from peers, anonymized and searchable.',
    body: 'Explore a growing collection of 5,000+ clinician-submitted inquiries. See how others are approaching tough cases and discover new perspectives on care.',
  },
  {
    title: 'Send an inquiry, get answers',
    heading: 'Ask a question and get a real answer.',
    body: 'Write your clinical question, attach relevant details, and choose an expert—or let us route it for you. Most responses arrive in under 4 hours.',
  },
];

/**
 * Tutorial steps with the welcome step addressed to the reader by name.
 *
 * The name extends the product greeting rather than replacing it, so the
 * version without a name is the same sentence cut short instead of a visibly
 * different headline. That keeps the fallback invisible to a reader with no
 * name on file, and keeps the product named on the first screen either way.
 *
 * Pass the formal short form a reader would be addressed by in person, not a
 * full name — this is a salutation.
 */
export const buildOnboardingSteps = (greetingName?: string | null): TutorialStep[] => [
  greetingName ? { ...welcomeStep, title: `${welcomeStep.title}, ${greetingName}` } : welcomeStep,
  ...remainingSteps,
];

const defaultSteps: TutorialStep[] = buildOnboardingSteps();

const tutorialImages = [Tutorial_1, Tutorial_2, Tutorial_3, Tutorial_4];

export function OnboardingTutorialModal({
  isOpen,
  onClose,
  onComplete,
  steps = defaultSteps,
  loadingPrimaryAction,
  markShown,
  markShowLater,
  userType,
}: OnboardingTutorialModalProps) {
  const [stepIndex, setStepIndex] = useState(0);

  const step = steps[stepIndex];

  const isLast = stepIndex === steps.length - 1;

  const track = useCallback(
    (event: keyof typeof ANALYTICS_EVENTS, props?: Record<string, unknown>) => {
      segmentClient.track(ANALYTICS_EVENTS[event], {
        stepIndex,
        totalSteps: steps.length,
        platform: Platform.OS,
        // Explicit over absent: Segment drops undefined, which would leave the
        // event out of a user-type breakdown rather than visible in it.
        user_type: userType ?? 'unknown',
        ...props,
      });
    },
    [stepIndex, steps.length, userType]
  );

  const handleClose = useCallback(async () => {
    track('ONBOARDING_TUTORIAL_CLOSED');

    await markShowLater?.();

    onClose();
  }, [onClose, track, markShowLater]);

  const handlePrimary = useCallback(async () => {
    if (isLast) {
      track('ONBOARDING_TUTORIAL_COMPLETED');

      await markShown?.();

      onComplete?.();

      onClose();
    } else {
      setStepIndex((i) => {
        const next = i + 1;

        track('ONBOARDING_TUTORIAL_STEP_VIEWED', { nextStep: next });

        return next;
      });
    }
  }, [isLast, onComplete, onClose, track, markShown]);

  const handleSecondary = useCallback(() => {
    setStepIndex((i) => {
      const prev = i - 1;

      track('ONBOARDING_TUTORIAL_BACK', { prevStep: prev });

      track('ONBOARDING_TUTORIAL_STEP_VIEWED', { stepIndex: prev });

      return prev;
    });
  }, [track]);

  const openedTracked = useRef(false);

  /**
   * One open event per opening, however many steps the reader moves through.
   *
   * It previously shared an effect with the step event, keyed on the step
   * index, so advancing re-fired both and an opening counted once per step —
   * a fourfold overcount that made the event unusable for the question it
   * exists to answer. The ref, rather than a narrower dependency list, is what
   * keeps the step index available for the payload without letting a change to
   * it re-fire the event.
   */
  useEffect(() => {
    if (!isOpen) {
      openedTracked.current = false;

      return;
    }

    if (openedTracked.current) {
      return;
    }

    openedTracked.current = true;

    track('ONBOARDING_TUTORIAL_OPENED', { stepIndex });
  }, [isOpen, stepIndex, track]);

  useEffect(() => {
    if (isOpen) {
      track('ONBOARDING_TUTORIAL_STEP_VIEWED', { stepIndex });
    }
  }, [isOpen, stepIndex, track]);

  const renderContent = () => (
    <VStack>
      <VStack
        className={twJoin(
          'h-80 w-full items-center justify-center overflow-hidden rounded-md',
          // The screenshots are the point of these steps, so the frame is as
          // tall as it can be. Height is what binds: the sources are near 16:9,
          // so a frame shorter than that letterboxes horizontally and wastes
          // the width it was given. The cap is viewport-relative because a
          // taller frame pushes the body copy below the fold on a short laptop
          // screen. Native has no viewport unit and no such gap.
          isWeb ? 'md:h-[52vh] md:max-h-[30rem] md:min-h-[18rem]' : 'md:h-96'
        )}>
        <Image
          source={tutorialImages[stepIndex] ?? tutorialImages[0]}
          contentFit="contain"
          style={{ width: '100%', height: '100%' }}
        />
      </VStack>
      {step.heading && (
        <Heading
          size="h3-md"
          className="mb-2 font-semibold text-base leading-normal text-typography-950">
          {step.heading}
        </Heading>
      )}
      {typeof step.body === 'string' ? (
        <Text className="mb-3 font-normal text-sm leading-normal text-typography-950">
          {step.body}
        </Text>
      ) : (
        step.body
      )}
      <HStack className="items-center justify-center gap-2">
        {steps.map((_, i) => (
          <VStack
            key={i}
            className={
              i === stepIndex
                ? 'h-2 w-2 rounded-full bg-primary-500'
                : 'h-2 w-2 rounded-full bg-outline-200'
            }
          />
        ))}
      </HStack>
    </VStack>
  );

  const renderFooter = () => (
    <HStack className="flex-row gap-2">
      {stepIndex > 0 && (
        <Button variant="outline" action="primary" onPress={handleSecondary} className="flex-1">
          <ButtonText className="font-medium text-sm leading-normal text-primary-500">
            Back
          </ButtonText>
        </Button>
      )}
      <Button
        onPress={handlePrimary}
        disabled={!!loadingPrimaryAction}
        className={stepIndex === 0 ? 'w-full' : 'flex-1'}>
        {loadingPrimaryAction ? <ButtonSpinner /> : null}
        <ButtonText className="font-medium text-sm leading-normal text-typography-0">
          {isLast ? 'Continue' : 'Next'}
        </ButtonText>
      </Button>
    </HStack>
  );

  const sheetMaxHeight = Math.round(Dimensions.get('window').height * 0.92);

  if (isWeb) {
    return (
      <Modal isOpen={isOpen} onClose={handleClose} size="lg">
        <ModalBackdrop />
        <ModalContent className="max-h-[92vh] w-full p-0 md:max-w-[960px]">
          <ModalHeader className="items-start px-6 pb-0 pt-5">
            <HStack className="w-full items-center justify-between">
              <Heading
                size="h2-md"
                className="my-0 font-semibold text-xl leading-normal text-typography-950 md:text-2xl">
                {step.title}
              </Heading>
              <Button
                variant="link"
                action="secondary"
                onPress={handleClose}
                accessibilityLabel="Close"
                className="-mr-3 -mt-3 h-12 w-12 items-center justify-center p-0">
                <Icon as={X} size="xl" className="text-typography-600" />
              </Button>
            </HStack>
          </ModalHeader>

          <ModalBody className="px-6 pb-0 pt-0">{renderContent()}</ModalBody>

          <ModalFooter className="flex-row gap-2 px-6 pb-5 pt-3">{renderFooter()}</ModalFooter>
        </ModalContent>
      </Modal>
    );
  }

  return (
    <Actionsheet isOpen={isOpen} onClose={handleClose}>
      <ActionsheetBackdrop />
      <ActionsheetContent
        className="w-full rounded-t-2xl p-0"
        style={{ maxHeight: sheetMaxHeight }}>
        <ActionsheetDragIndicatorWrapper>
          <ActionsheetDragIndicator />
        </ActionsheetDragIndicatorWrapper>

        <ActionsheetScrollView contentContainerClassName="px-6 pb-16">
          <HStack className="w-full items-center justify-between border-b border-background-200 py-3">
            <Heading
              size="h2-md"
              className="font-semibold text-xl leading-normal text-typography-950 md:text-2xl">
              {step.title}
            </Heading>
            <Pressable
              onPress={handleClose}
              accessibilityRole="button"
              accessibilityLabel="Close"
              className="-mr-2 shrink-0 p-2">
              <Icon as={X} className="h-7 w-7 text-typography-600" />
            </Pressable>
          </HStack>

          <VStack className="pt-4">{renderContent()}</VStack>

          <VStack className="mt-6">{renderFooter()}</VStack>
        </ActionsheetScrollView>
      </ActionsheetContent>
    </Actionsheet>
  );
}
